/**
 * ARIA Live Region Manager for Screen Reader Announcements
 * Manages polite and assertive live regions for accessibility compliance
 */

class LiveRegionManager {
    constructor() {
        this.regions = new Map();
        this.initialized = false;
        this.messageQueue = [];
        this.isProcessing = false;
    }

    /**
     * Initialize live regions
     */
    init() {
        if (this.initialized) return;

        // Create default live regions
        this.createRegion('polite', 'polite');
        this.createRegion('assertive', 'assertive');
        this.createRegion('status', 'polite', 'status');
        this.createRegion('alert', 'assertive', 'alert');
        this.createRegion('log', 'polite', 'log');

        this.initialized = true;
        
        // Process any queued messages
        this.processQueue();
    }

    /**
     * Create a live region
     * @param {string} id - Unique identifier for the region
     * @param {string} politeness - 'polite' or 'assertive'
     * @param {string} role - ARIA role (optional)
     * @returns {Element} - Created live region element
     */
    createRegion(id, politeness = 'polite', role = null) {
        // Remove existing region if it exists
        if (this.regions.has(id)) {
            this.removeRegion(id);
        }

        const region = document.createElement('div');
        region.id = `live-region-${id}`;
        region.setAttribute('aria-live', politeness);
        region.setAttribute('aria-atomic', 'true');
        region.className = 'sr-only live-region';
        
        if (role) {
            region.setAttribute('role', role);
        }

        // Add to DOM
        document.body.appendChild(region);
        
        // Store reference
        this.regions.set(id, {
            element: region,
            politeness,
            role,
            lastMessage: '',
            lastMessageTime: 0
        });

        return region;
    }

    /**
     * Remove a live region
     * @param {string} id - Region identifier
     */
    removeRegion(id) {
        const region = this.regions.get(id);
        if (region && region.element.parentNode) {
            region.element.parentNode.removeChild(region.element);
        }
        this.regions.delete(id);
    }

    /**
     * Announce a message in a live region
     * @param {string} message - Message to announce
     * @param {Object} options - Announcement options
     */
    announce(message, options = {}) {
        const {
            politeness = 'polite',
            role = null,
            priority = 'normal',
            deduplicate = true,
            delay = 0,
            clear = false
        } = options;

        // Queue message if not initialized
        if (!this.initialized) {
            this.messageQueue.push({ message, options });
            return;
        }

        // Determine region to use
        let regionId = politeness;
        if (role) {
            regionId = role;
        }

        // Get or create region
        let region = this.regions.get(regionId);
        if (!region) {
            this.createRegion(regionId, politeness, role);
            region = this.regions.get(regionId);
        }

        // Check for duplicate messages
        if (deduplicate && this.isDuplicate(regionId, message)) {
            return;
        }

        // Handle announcement
        if (delay > 0) {
            setTimeout(() => {
                this.performAnnouncement(regionId, message, clear);
            }, delay);
        } else {
            this.performAnnouncement(regionId, message, clear);
        }
    }

    /**
     * Perform the actual announcement
     * @param {string} regionId - Region identifier
     * @param {string} message - Message to announce
     * @param {boolean} clear - Whether to clear previous content
     */
    performAnnouncement(regionId, message, clear = false) {
        const region = this.regions.get(regionId);
        if (!region) return;

        // Clear previous content if requested
        if (clear) {
            region.element.textContent = '';
        }

        // Use a slight delay to ensure screen readers pick up the change
        setTimeout(() => {
            if (clear || !region.element.textContent) {
                region.element.textContent = message;
            } else {
                // Append to existing content
                region.element.textContent += '. ' + message;
            }

            // Update tracking
            region.lastMessage = message;
            region.lastMessageTime = Date.now();
        }, 50);
    }

    /**
     * Check if message is a duplicate
     * @param {string} regionId - Region identifier
     * @param {string} message - Message to check
     * @returns {boolean}
     */
    isDuplicate(regionId, message) {
        const region = this.regions.get(regionId);
        if (!region) return false;

        // Check if same message was announced recently (within 2 seconds)
        const timeSinceLastMessage = Date.now() - region.lastMessageTime;
        return region.lastMessage === message && timeSinceLastMessage < 2000;
    }

    /**
     * Process queued messages
     */
    processQueue() {
        if (this.isProcessing) return;
        this.isProcessing = true;

        while (this.messageQueue.length > 0) {
            const { message, options } = this.messageQueue.shift();
            this.announce(message, options);
        }

        this.isProcessing = false;
    }

    /**
     * Clear all live regions
     */
    clearAll() {
        this.regions.forEach(region => {
            region.element.textContent = '';
        });
    }

    /**
     * Clear specific live region
     * @param {string} regionId - Region identifier
     */
    clear(regionId) {
        const region = this.regions.get(regionId);
        if (region) {
            region.element.textContent = '';
        }
    }

    /**
     * Announce loading state
     * @param {string} action - Description of what's loading
     */
    announceLoading(action = 'Loading') {
        this.announce(`${action}, please wait`, {
            politeness: 'polite',
            role: 'status'
        });
    }

    /**
     * Announce completion of loading
     * @param {string} action - Description of what completed
     * @param {string} result - Result description (optional)
     */
    announceComplete(action = 'Loading complete', result = '') {
        const message = result ? `${action}. ${result}` : action;
        this.announce(message, {
            politeness: 'polite',
            role: 'status'
        });
    }

    /**
     * Announce error
     * @param {string} error - Error message
     * @param {boolean} critical - Whether error is critical
     */
    announceError(error, critical = false) {
        this.announce(error, {
            politeness: critical ? 'assertive' : 'polite',
            role: 'alert'
        });
    }

    /**
     * Announce success
     * @param {string} message - Success message
     */
    announceSuccess(message) {
        this.announce(message, {
            politeness: 'polite',
            role: 'status'
        });
    }

    /**
     * Announce navigation change
     * @param {string} location - New location
     * @param {string} pageTitle - Page title (optional)
     */
    announceNavigation(location, pageTitle = '') {
        const message = pageTitle 
            ? `Navigated to ${pageTitle}` 
            : `Navigated to ${location}`;
        
        this.announce(message, {
            politeness: 'polite',
            role: 'status',
            delay: 500 // Give time for page to load
        });
    }

    /**
     * Announce form validation
     * @param {Object} validation - Validation result
     */
    announceValidation(validation) {
        const { isValid, errors, fieldName } = validation;
        
        if (isValid) {
            this.announceSuccess(`${fieldName} is valid`);
        } else {
            const errorMessage = errors.length === 1 
                ? `${fieldName}: ${errors[0]}`
                : `${fieldName} has ${errors.length} errors: ${errors.join(', ')}`;
            
            this.announceError(errorMessage, true);
        }
    }

    /**
     * Announce dynamic content change
     * @param {string} change - Description of the change
     * @param {number} count - Number of items (optional)
     */
    announceContentChange(change, count = null) {
        let message = change;
        if (count !== null) {
            message += `. ${count} ${count === 1 ? 'item' : 'items'}`;
        }
        
        this.announce(message, {
            politeness: 'polite',
            role: 'status'
        });
    }

    /**
     * Announce modal or dialog state
     * @param {boolean} opened - Whether modal opened or closed
     * @param {string} title - Modal title
     */
    announceModal(opened, title = 'Modal') {
        const message = opened 
            ? `${title} dialog opened` 
            : `${title} dialog closed`;
        
        this.announce(message, {
            politeness: 'polite',
            role: 'status'
        });
    }

    /**
     * Announce progress update
     * @param {number} current - Current progress value
     * @param {number} total - Total/maximum value
     * @param {string} label - Progress label
     */
    announceProgress(current, total, label = 'Progress') {
        const percentage = Math.round((current / total) * 100);
        const message = `${label}: ${percentage}% complete`;
        
        // Only announce at certain intervals to avoid spam
        if (percentage % 10 === 0 || percentage === 100) {
            this.announce(message, {
                politeness: 'polite',
                role: 'status',
                deduplicate: true
            });
        }
    }

    /**
     * Announce search results
     * @param {number} count - Number of results
     * @param {string} query - Search query
     */
    announceSearchResults(count, query = '') {
        let message;
        if (count === 0) {
            message = query ? `No results found for "${query}"` : 'No results found';
        } else if (count === 1) {
            message = query ? `1 result found for "${query}"` : '1 result found';
        } else {
            message = query ? `${count} results found for "${query}"` : `${count} results found`;
        }
        
        this.announce(message, {
            politeness: 'polite',
            role: 'status'
        });
    }

    /**
     * Batch announce multiple messages with delays
     * @param {Array} messages - Array of {message, options, delay} objects
     */
    batchAnnounce(messages) {
        messages.forEach(({ message, options = {}, delay = 0 }, index) => {
            const totalDelay = delay + (index * 100); // Stagger announcements
            setTimeout(() => {
                this.announce(message, options);
            }, totalDelay);
        });
    }

    /**
     * Get debug information about live regions
     * @returns {Object} - Debug information
     */
    getDebugInfo() {
        const regions = {};
        this.regions.forEach((region, id) => {
            regions[id] = {
                politeness: region.politeness,
                role: region.role,
                lastMessage: region.lastMessage,
                lastMessageTime: region.lastMessageTime,
                currentContent: region.element.textContent
            };
        });

        return {
            initialized: this.initialized,
            regionCount: this.regions.size,
            queuedMessages: this.messageQueue.length,
            regions
        };
    }
}

// Global instance
export const liveRegionManager = new LiveRegionManager();

// Initialize when DOM is ready
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => liveRegionManager.init());
    } else {
        liveRegionManager.init();
    }
}

// Vue plugin
export const LiveRegionPlugin = {
    install(Vue) {
        Vue.prototype.$announce = (message, options) => {
            liveRegionManager.announce(message, options);
        };

        Vue.prototype.$announceLoading = (action) => {
            liveRegionManager.announceLoading(action);
        };

        Vue.prototype.$announceComplete = (action, result) => {
            liveRegionManager.announceComplete(action, result);
        };

        Vue.prototype.$announceError = (error, critical) => {
            liveRegionManager.announceError(error, critical);
        };

        Vue.prototype.$announceSuccess = (message) => {
            liveRegionManager.announceSuccess(message);
        };

        Vue.prototype.$announceNavigation = (location, pageTitle) => {
            liveRegionManager.announceNavigation(location, pageTitle);
        };

        Vue.prototype.$announceValidation = (validation) => {
            liveRegionManager.announceValidation(validation);
        };

        Vue.prototype.$announceContentChange = (change, count) => {
            liveRegionManager.announceContentChange(change, count);
        };

        Vue.prototype.$announceModal = (opened, title) => {
            liveRegionManager.announceModal(opened, title);
        };

        Vue.prototype.$announceProgress = (current, total, label) => {
            liveRegionManager.announceProgress(current, total, label);
        };

        Vue.prototype.$announceSearchResults = (count, query) => {
            liveRegionManager.announceSearchResults(count, query);
        };
    }
};

export default liveRegionManager;